/**
 * Client-side validation for the password change: deliberately thin.
 *
 * Only two rules can be decided here: the length, and whether the confirmation
 * matches. The rest of what the backend enforces needs knowledge this side does
 * not have; "must differ from the current one" needs the stored hash, and the
 * breached-password screening needs a call to Have I Been Pwned. Both come back
 * as a 400 and are placed on the right field by `password-change-error.ts`.
 * Validating here is about sparing a round-trip for the cases we can be sure of,
 * not about reimplementing the policy.
 *
 * NOT reusing `features/auth/_shared/domain/password.validation.ts`: it lives in
 * the auth area's shared module, and an `account` slice importing it would break
 * the hermeticity the feature README defends. The part that must never drift
 * (the two numbers) is imported from `@shared/constants/password`, the same
 * source the backend domain rule and the Fastify schemas read. Only the
 * comparison is written twice.
 */
import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH } from '@shared/constants/password';

import type { PasswordChangeDraft, PasswordValidationErrors } from './password-change.model';

export { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH };

/** UTF-8 byte length; bcrypt's real boundary, mirrored from the backend policy. */
const byteLength = (s: string): number => new TextEncoder().encode(s).length;

/**
 * Validate the draft. The current password is only checked for presence: its
 * correctness is the server's answer (a 401), and guessing at it here would only
 * teach the form to be wrong.
 *
 * The length rules report BEFORE the mismatch (same ordering as
 * `validatePasswordPair` in the auth area) so a password that is merely too
 * short does not also accuse the confirmation, which is only half-typed anyway
 * while the user is still going.
 */
export function validateDraft(draft: PasswordChangeDraft): PasswordValidationErrors {
  const errors: PasswordValidationErrors = {};

  if (draft.currentPassword.length === 0) {
    errors.currentPassword = 'Enter your current password.';
  }

  if (draft.newPassword.length < MIN_PASSWORD_LENGTH) {
    errors.newPassword = `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  } else if (byteLength(draft.newPassword) > MAX_PASSWORD_LENGTH) {
    errors.newPassword = `Password must be at most ${MAX_PASSWORD_LENGTH} characters.`;
  } else if (draft.newPassword !== draft.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match.';
  }

  return errors;
}

/** Is the draft submittable? Cheap enough to call on every keystroke. */
export function isDraftValid(draft: PasswordChangeDraft): boolean {
  return Object.keys(validateDraft(draft)).length === 0;
}
